import {
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  FileOutlined,
  FilePdfOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import {
  Button,
  Image,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Tooltip,
  Typography,
  Upload,
  type UploadFile,
} from 'antd';
import React, { useCallback, useEffect, useState } from 'react';

import { ENotificationSeverity } from '../../../admin-types';
import { type IAttachmentMeta } from '../../../api';
import { type NotifyType, useNotify } from '../../notification';
import { FONT_SIZE, SPACING } from '../../theme/tokens';

const { Dragger } = Upload;

const IMAGE_MIMETYPES: Set<string> = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/tiff',
  'image/bmp',
]);

const PDF_MIMETYPE = 'application/pdf';

const PDF_PREVIEW_WIDTH = '96vw';

const PDF_PREVIEW_HEIGHT = 'calc(100vh - 10rem)';

const ORDER_DOCUMENTS_PATTERN = /^ropere-order-documents-(.+)-([a-z]{2})\.pdf$/;

export const getAttachmentLabel = (filename: string): string => {
  const match: RegExpExecArray | null = ORDER_DOCUMENTS_PATTERN.exec(filename);

  return match
    ? `Order documents (terms ${match[1]}, ${match[2]?.toUpperCase()})`
    : filename;
};

export const getAttachmentSrc = (
  orderUUID: string,
  attachment: IAttachmentMeta,
): string =>
  `/static/order/attachment/${orderUUID.slice(0, 2)}/${orderUUID.slice(2, 4)}/${orderUUID}/${attachment.storedAs}`;

export const isImageAttachment = (attachment: IAttachmentMeta): boolean =>
  IMAGE_MIMETYPES.has(attachment.mimetype);

export const isPdfAttachment = (attachment: IAttachmentMeta): boolean =>
  attachment.mimetype === PDF_MIMETYPE;

function Attachments({
  orderUUID,
  onTogglePublic,
  refreshKey,
}: {
  orderUUID: string;
  onTogglePublic: (attachmentUUID: string, isPublic: boolean) => void;
  refreshKey?: string;
}): React.ReactElement {
  const notify: NotifyType = useNotify();
  const [attachments, setAttachments] = useState<IAttachmentMeta[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<UploadFile[]>([]);
  const [previewUUID, setPreviewUUID] = useState<string | null>(null);

  const pdfPreview: IAttachmentMeta | undefined = attachments.find(
    (a: IAttachmentMeta): boolean =>
      a.uuid === previewUUID && isPdfAttachment(a),
  );

  const fetchAttachments = useCallback(async (): Promise<void> => {
    try {
      const res = await fetch(`/api/order/${orderUUID}/attachments`);
      const data: IAttachmentMeta[] = await res.json();
      setAttachments(data);
    } catch {
      // ignore
    }
  }, [orderUUID]);

  useEffect((): void => {
    fetchAttachments();
  }, [fetchAttachments, refreshKey]);

  const handleDelete = async (uuid: string): Promise<void> => {
    const res = await fetch(`/api/order/${orderUUID}/attachment/${uuid}`, {
      method: 'DELETE',
    });

    if (res.ok) {
      notify(ENotificationSeverity.SUCCESS, 'Attachment deleted.');
      setAttachments((prev) => prev.filter((a) => a.uuid !== uuid));
    } else {
      notify(ENotificationSeverity.ERROR, 'Failed to delete attachment.');
    }
  };

  const handleTogglePublic = (uuid: string, isPublic: boolean): void => {
    onTogglePublic(uuid, isPublic);
    setAttachments((prev) =>
      prev.map((a) => (a.uuid === uuid ? { ...a, isPublic } : a)),
    );
  };

  return (
    <Space orientation="vertical" style={{ width: '100%' }}>
      <Dragger
        name="attachment"
        multiple
        action={`/api/order/${orderUUID}/attachment`}
        withCredentials
        fileList={uploadingFiles}
        showUploadList={{ showRemoveIcon: false }}
        onChange={(info): void => {
          setUploadingFiles(
            info.fileList.filter((f) => f.status === 'uploading'),
          );

          if (info.file.status === 'done') {
            notify(
              ENotificationSeverity.SUCCESS,
              `${info.file.name} uploaded.`,
            );
            fetchAttachments();
          } else if (info.file.status === 'error') {
            notify(
              ENotificationSeverity.ERROR,
              `${info.file.name} upload failed.`,
            );
          }
        }}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">
          Click or drag files here to upload attachments
        </p>
        <p className="ant-upload-hint">
          Images are automatically converted to WebP. PDFs and other files are
          stored as-is.
        </p>
      </Dragger>

      {attachments.length > 0 && (
        <div>
          {attachments.map((item: IAttachmentMeta): React.ReactElement => {
            const isImage: boolean = isImageAttachment(item);
            const isPdf: boolean = isPdfAttachment(item);
            const src: string = getAttachmentSrc(orderUUID, item);

            return (
              <div
                key={item.uuid}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '4px 0',
                }}
              >
                <Space>
                  {isImage && (
                    <Image
                      src={src}
                      width={48}
                      height={48}
                      style={{ objectFit: 'cover' }}
                      preview={{
                        open: previewUUID === item.uuid,
                        onOpenChange: (open: boolean): void => {
                          setPreviewUUID(open ? item.uuid : null);
                        },
                      }}
                    />
                  )}
                  {isPdf && (
                    <FilePdfOutlined
                      aria-label={`Preview ${item.filename}`}
                      style={{ fontSize: FONT_SIZE.xxl, cursor: 'pointer' }}
                      onClick={(): void => setPreviewUUID(item.uuid)}
                    />
                  )}
                  {!isImage && !isPdf && (
                    <FileOutlined style={{ fontSize: FONT_SIZE.xxl }} />
                  )}
                  <Space orientation="vertical" size={SPACING.none}>
                    <Typography.Text strong>
                      {getAttachmentLabel(item.filename)}
                    </Typography.Text>
                    <Typography.Text type="secondary">
                      {(item.size / 1024).toFixed(1)} KB &middot;{' '}
                      {item.storedAs}
                    </Typography.Text>
                  </Space>
                </Space>
                <Space size={SPACING.xs} align="center">
                  {(isImage || isPdf) && (
                    <Tooltip title="Preview">
                      <Button
                        type="text"
                        aria-label={`Preview ${item.filename}`}
                        icon={<EyeOutlined />}
                        onClick={(): void => setPreviewUUID(item.uuid)}
                      />
                    </Tooltip>
                  )}
                  <Tooltip title="Download">
                    <Button
                      type="text"
                      aria-label={`Download ${item.filename}`}
                      icon={<DownloadOutlined />}
                      href={src}
                      download={item.filename}
                    />
                  </Tooltip>
                  <Switch
                    size="small"
                    checked={item.isPublic}
                    checkedChildren="Public"
                    unCheckedChildren="Private"
                    onChange={(checked): void =>
                      handleTogglePublic(item.uuid, checked)
                    }
                  />
                  <Popconfirm
                    title={`Delete ${item.filename}?`}
                    okText="Delete"
                    okButtonProps={{ danger: true }}
                    onConfirm={(): Promise<void> => handleDelete(item.uuid)}
                  >
                    <Tooltip title="Delete attachment">
                      <Button type="text" danger icon={<DeleteOutlined />} />
                    </Tooltip>
                  </Popconfirm>
                </Space>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        open={!!pdfPreview}
        title={pdfPreview ? getAttachmentLabel(pdfPreview.filename) : null}
        width={PDF_PREVIEW_WIDTH}
        centered
        destroyOnHidden
        onCancel={(): void => setPreviewUUID(null)}
        footer={
          pdfPreview ? (
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              href={getAttachmentSrc(orderUUID, pdfPreview)}
              download={pdfPreview.filename}
            >
              Download
            </Button>
          ) : null
        }
      >
        {pdfPreview && (
          <iframe
            title={pdfPreview.filename}
            src={getAttachmentSrc(orderUUID, pdfPreview)}
            style={{ width: '100%', height: PDF_PREVIEW_HEIGHT, border: 0 }}
          />
        )}
      </Modal>
    </Space>
  );
}

export default React.memo(Attachments);
